import React, { useMemo } from 'react';
import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Registración idempotente — react-chartjs-2 acepta múltiples registers.
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const CHART_COLORS = [
  '#073C5C', '#F68D2E', '#10b981', '#3b82f6', '#8b5cf6',
  '#ec4899', '#f59e0b', '#06b6d4', '#84cc16', '#ef4444',
];

const ChartWidget = ({ data, chartType }) => {
  const { labels = [], datasets = [], summary } = data || {};
  const effectiveType = (data?.chart_type || chartType || 'bar').toLowerCase();

  const chartData = useMemo(() => {
    if (!labels.length || !datasets.length) return null;

    const isCircular = effectiveType === 'doughnut' || effectiveType === 'pie';

    return {
      labels,
      datasets: datasets.map((ds, idx) => {
        const baseColor = CHART_COLORS[idx % CHART_COLORS.length];
        if (isCircular) {
          return {
            label: ds.label,
            data: ds.data,
            backgroundColor: ds.data.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
            borderColor: '#fff',
            borderWidth: 2,
          };
        }
        if (effectiveType === 'line') {
          return {
            label: ds.label,
            data: ds.data,
            borderColor: baseColor,
            backgroundColor: `${baseColor}33`,
            tension: 0.3,
            fill: true,
            pointRadius: 4,
            pointBackgroundColor: baseColor,
          };
        }
        // bar
        return {
          label: ds.label,
          data: ds.data,
          backgroundColor: ds.data.map((v) =>
            // Color del bar según magnitud: rojo si negativo y > -10%, naranja moderado, verde si positivo
            typeof v === 'number' && v < -5 ? '#ef4444' :
            typeof v === 'number' && v < 0  ? '#f59e0b' :
            typeof v === 'number' && v > 5  ? '#10b981' :
            baseColor
          ),
          borderRadius: 6,
        };
      }),
    };
  }, [labels, datasets, effectiveType]);

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: effectiveType === 'doughnut' || effectiveType === 'pie' ? 'right' : 'top',
        labels: { font: { size: 11 }, boxWidth: 12, padding: 12 },
      },
      tooltip: { padding: 10, cornerRadius: 8 },
    },
  };

  const cartesianOptions = {
    ...commonOptions,
    scales: {
      y: { beginAtZero: true, ticks: { font: { size: 11 } } },
      x: { ticks: { font: { size: 11 }, maxRotation: 30, minRotation: 0 } },
    },
  };

  if (!chartData) {
    return (
      <div className="chart-widget-empty">
        <p>{summary || 'Sin datos suficientes para graficar.'}</p>
      </div>
    );
  }

  const ChartComponent =
    effectiveType === 'doughnut' ? Doughnut :
    effectiveType === 'pie'      ? Pie :
    effectiveType === 'line'     ? Line :
    Bar;

  const opts = (effectiveType === 'doughnut' || effectiveType === 'pie') ? commonOptions : cartesianOptions;

  return (
    <div className="chart-widget-body">
      <div className="chart-widget-canvas">
        <ChartComponent data={chartData} options={opts} />
      </div>
      {summary && <p className="chart-widget-summary">{summary}</p>}
    </div>
  );
};

export default ChartWidget;
